import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@app/data-layer/enums/documents';
import { CompanyModel } from '@app/data-layer/models/companies';
import { DocumentModel, DocumentCreateModel } from '@app/data-layer/models/documents';
import { EntrySummaryModel } from '@app/data-layer/models/entry-summaries';
import { POA_FILE_URL } from '@constants/constants';
import { enumFileExtension } from '@enum/enumFileExtension';
import { enumMimeType } from '@enum/enumMimeType';
import { ReplaySubject } from 'rxjs';
import { DigitalSignsRepository } from '@data-layer/repositories/digital-signs.repository';
import { first, finalize, takeUntil } from 'rxjs/operators';
import { DocumentsService } from '@app/shared/services/documents.service';
import { CompanyPowerAttorneyComponent } from '@app/modules/company/components/company-power-attorney/company-power-attorney.component';

@Component({
  selector: 'app-es-poa-sidebar',
  templateUrl: './es-poa-sidebar.component.html',
  styleUrls: ['./es-poa-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsPoaSidebarComponent implements OnInit, OnDestroy {
  @Input() public company: CompanyModel;
  private _entrySummary: EntrySummaryModel;
  @Input()
  public set entrySummary(value: EntrySummaryModel) {
    this._entrySummary = value;
    this.updatePoaPurchaseReturnUrl();
  }
  public get entrySummary(): EntrySummaryModel {
    return this._entrySummary;
  }
  @Input() public headerTitle: string = 'DOCUMENT_TYPE.POWER_OF_ATTORNEY';
  @Input() public shipmentId: number;
  @Input() public docType: enumDocumentType;

  @Output() public close: EventEmitter<void> = new EventEmitter<void>();
  @Output() public saveSelectedPoa: EventEmitter<{ document: DocumentModel; docType: enumDocumentType }> = new EventEmitter<{
    document: DocumentModel;
    docType: enumDocumentType;
  }>();

  public fileMimeIgnoreList: enumMimeType[] = [enumMimeType.XLS];
  public fileExtensionIgnoreList: enumFileExtension[] = [enumFileExtension.XLS];
  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public POA_FILE_URL: typeof POA_FILE_URL = POA_FILE_URL;
  public dontSaveForFutureFilingsControl: UntypedFormControl;
  public isLoadingDocUpload: boolean;
  public inputFile: File;

  public document: DocumentModel;
  public digitalCompanyPoa: DocumentModel | null = null;
  public manualCompanyPoa: DocumentModel | null = null;
  public selectedPoaVariant: 'digital' | 'manual' | null = null;
  public poaPurchaseReturnUrl: string;
  public isLoadingCompanyPoa: boolean = false;

  @ViewChild(CompanyPowerAttorneyComponent) public poaComponent: CompanyPowerAttorneyComponent;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private digitalSignsRepository: DigitalSignsRepository,
    private documentsService: DocumentsService,
  ) {}

  ngOnInit(): void {
    this.initializeDocument();

    this.dontSaveForFutureFilingsControl = new UntypedFormControl(false);

    this.initializeDontSaveForFutureFilings();

    this.updatePoaPurchaseReturnUrl();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get isSaveDisabled(): boolean {
    const hasAny = !!this.digitalCompanyPoa || !!this.manualCompanyPoa;
    return !hasAny || !this.selectedPoaVariant || !this.entrySummary?.id;
  }

  public onPoaAvailabilityChange(payload: { digital: DocumentModel | null; manual: DocumentModel | null }): void {
    this.digitalCompanyPoa = payload.digital;
    this.manualCompanyPoa = payload.manual;
    if (!this.selectedPoaVariant) {
      if (this.digitalCompanyPoa) {
        this.selectedPoaVariant = 'digital';
      } else if (this.manualCompanyPoa) {
        this.selectedPoaVariant = 'manual';
      } else {
        this.selectedPoaVariant = null;
      }
    }
    this.cdr.markForCheck();
  }

  public onSelectedPoaVariantChange(variant: 'digital' | 'manual' | null): void {
    this.selectedPoaVariant = variant;
    this.cdr.markForCheck();
  }

  public onRequestSaveBeforeSign(): void {
    if (!this.poaComponent) {
      return;
    }
    const companyId: number | undefined = this.company?.id;
    if (companyId === undefined) {
      this.poaComponent.cancelWaitingForSave();
      this.cdr.markForCheck();
      return;
    }
    this.poaComponent.proceedAfterSave(companyId);
    this.cdr.markForCheck();
  }

  public onManualPoaUpload(file: File): void {
    if (!file) {
      return;
    }
    this.manualCompanyPoa = null;
    this.setCompanyPoaLoading(true);
    this.uploadDocumentToCompany(file, enumDocumentType.POWER_OF_ATTORNEY, () => this.setCompanyPoaLoading(false));
  }

  public onManualPoaStateUpdate(state: enumDocumentState): void {
    if (this.manualCompanyPoa) {
      this.manualCompanyPoa = {
        ...this.manualCompanyPoa,
        state,
      };
      this.cdr.markForCheck();
    }
  }

  public onSave(): void {
    if (this.isSaveDisabled) {
      return;
    }

    const selected =
      this.selectedPoaVariant === 'digital' ? this.digitalCompanyPoa : this.selectedPoaVariant === 'manual' ? this.manualCompanyPoa : null;

    if (!selected) {
      return;
    }

    const docType = this.docType || enumDocumentType.POWER_OF_ATTORNEY;

    this.saveSelectedPoa.emit({ document: selected, docType });
  }

  private initializeDocument() {
    this.document = {
      id: -1,
      rootEntityId: this.entrySummary.id,
      rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
      relatedEntityId: this.shipmentId,
      relatedEntityType: enumDocumentRelatedEntityType.SHIPMENT,
      url: null,
      createdDate: null,
      fileName: null,
      type: this.docType,
      state: null,
    };
  }

  private updatePoaPurchaseReturnUrl(): void {
    const reference = this.entrySummary?.referenceNumber;
    this.poaPurchaseReturnUrl = reference ? `/entry-summary/${reference}?openPoaSidebar=true` : null;
  }

  private initializeDontSaveForFutureFilings(): void {
    const companyId = this.company?.id;
    if (!companyId) {
      return;
    }

    this.digitalSignsRepository
      .list(companyId)
      .pipe(first())
      .subscribe((items) => {
        if ((items || []).length > 0) {
          this.dontSaveForFutureFilingsControl.setValue(true, { emitEvent: false });
          this.dontSaveForFutureFilingsControl.disable({ emitEvent: false });
          this.cdr.markForCheck();
        }
      });
  }

  private uploadDocumentToCompany(file: File, docType: enumDocumentType, onComplete: () => void): void {
    const companyId = this.company?.id;
    if (!companyId) {
      onComplete();
      return;
    }

    this.documentsService
      .uploadDocument(this.buildCompanyDocumentPayload(docType, file.name, companyId), file, companyId)
      .pipe(takeUntil(this.destroy$), finalize(onComplete))
      .subscribe({
        next: () => {
          this.cdr.markForCheck();
        },
        error: () => {
          // noop - errors surfaced elsewhere; ensure UI unblocks
        },
      });
  }

  private buildCompanyDocumentPayload(docType: enumDocumentType, fileName: string, companyId: number): DocumentCreateModel {
    return {
      fileName,
      relatedEntityId: companyId,
      relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
      rootEntityId: companyId,
      rootEntityType: enumDocumentRootEntityType.COMPANY,
      type: Number(docType),
    };
  }

  private setCompanyPoaLoading(isLoading: boolean): void {
    this.isLoadingCompanyPoa = isLoading;
    this.cdr.markForCheck();
  }
}
